import * as firebase from "firebase/app";
import {
  FirestoreCollectionNames,
  FirestoreSharedKeys
} from "shared/firestore";
import {
  IApplication,
  IUnassignedApplicationMetadata,
  IUninitaliedApplication,
  IApplicationPath
} from "shared/model/IApplication";
import {
  ICoworkGeoLocation,
  IGooglePlace
} from "shared/model/ICoworkGeoLocation";
import { ICoworkBranch, ICoworkSpace } from "shared/model/ICoworkSpace";
import { IWeworkMember } from "shared/model/IWeworkMember";

import Validator from "../../Validators/Validator";
import FirebaseAuthService from "../auth/FirebaseAuthService";
import FirestoreService from "shared/services/firebase/FirestoreService";
import { ILead } from "shared/model/signup/ILead";

// ts get's weird about arrow funcs with generic type
// tslint:disable-next-line:only-arrow-functions
const mapTo = function<T>(
  items: firebase.firestore.QueryDocumentSnapshot[]
): T[] {
  return items.map(item => {
    const data = item.data() as any;
    return {
      ...data,
      uid: item.id
    };
  });
};

const rejectUserNotLoggedIn = () =>
  Promise.reject(`Can't get data without logged in user`);

export default class FirebaseDatabaseService {
  public static createUnassingnedApplicationId() {
    const doc = firebase
      .firestore()
      .collection(FirestoreCollectionNames.UnassignedApplications)
      .doc();
    return doc.id;
  }

  public static async addApplication(
    spaceName: string,
    application: IUninitaliedApplication
  ): Promise<IApplication> {
    const db = firebase.firestore();
    const applicationsRef = db
      .collection(FirestoreCollectionNames.CoworkingSpaces)
      .doc(spaceName)
      .collection(FirestoreCollectionNames.Applications)
      .doc();

    const intiializedApplication = { ...application, uid: applicationsRef.id };

    await applicationsRef.set(intiializedApplication);
    return intiializedApplication;
  }

  public static async getMyCoworkingSpaces(
    limit: number = 10
  ): Promise<ICoworkSpace[]> {
    const currentUser = FirebaseAuthService.currentUserBasicData();
    if (!currentUser) {
      return rejectUserNotLoggedIn();
    }

    return FirebaseDatabaseService.getCoworkingSpaces(
      Validator.emailDomain(currentUser.email)
    );
  }

  public static async getCoworkingSpaceBranch(
    coworkName: string,
    branchName: string
  ): Promise<ICoworkBranch> {
    if (!coworkName) {
      return Promise.reject(`Invalid cowork name ${coworkName}`);
    }

    const db = firebase.firestore();
    const branches = db
      .collection(FirestoreCollectionNames.CoworkingSpaces)
      .doc(coworkName)
      .collection(FirestoreCollectionNames.CoworkBranches)
      .doc(branchName);

    const branchSnapshot = await branches.get();
    return branchSnapshot.data() as ICoworkBranch;
  }

  public static async getAllCoworkingSpaceBranches(
    coworkName: string
  ): Promise<ICoworkBranch[]> {
    if (!coworkName) {
      return Promise.reject(`Invalid cowork name ${coworkName}`);
    }

    const db = firebase.firestore();
    const branches = db
      .collection(FirestoreCollectionNames.CoworkingSpaces)
      .doc(coworkName)
      .collection(FirestoreCollectionNames.CoworkBranches);

    const branchesSnapshot = await branches.get();
    return mapTo<ICoworkBranch>(branchesSnapshot.docs);
  }

  public static async getCoworkingSpacesBranches(
    coworkName: string,
    limit: number = 15
  ): Promise<ICoworkBranch[]> {
    if (!coworkName) {
      return Promise.reject(
        "Please pass a coworkName to getCoworkingSpacesBranches"
      );
    }

    const db = firebase.firestore();
    const myItems = db
      .collection(FirestoreCollectionNames.CoworkingSpaces)
      .doc(coworkName)
      .collection(FirestoreCollectionNames.CoworkBranches)
      .limit(limit);

    const data = await myItems.get();
    return mapTo<ICoworkBranch>(data.docs);
  }

  public static async getCoworkingSpaces(
    domain: string,
    limit: number = 10
  ): Promise<ICoworkSpace[]> {
    if (!domain) {
      return Promise.reject("Please pass a valid domain to getCoworkingSpaces");
    }

    const db = firebase.firestore();
    const myItems = db
      .collection(FirestoreCollectionNames.CoworkingSpaces)
      .where(FirestoreSharedKeys.Domain, "==", domain);

    const data = await myItems.get();
    return mapTo<ICoworkSpace>(data.docs);
  }

  public static async getApplications(
    coworkName: string,
    branchName: string | null,
    limit: number = 20
  ): Promise<IApplication[]> {
    const currentUser = FirebaseAuthService.currentUserBasicData();
    if (!currentUser) {
      return rejectUserNotLoggedIn();
    }

    const db = firebase.firestore();
    let myItemsQuery = db
      .collection(FirestoreCollectionNames.CoworkingSpaces)
      .doc(coworkName)
      .collection(FirestoreCollectionNames.Applications)
      .limit(limit)
      .orderBy(FirestoreSharedKeys.CreatedAt, "desc");

    if (branchName) {
      myItemsQuery = myItemsQuery.where(
        FirestoreSharedKeys.BranchName,
        "==",
        branchName
      );
    }

    const data = await myItemsQuery.get();
    return mapTo<IApplication>(data.docs);
  }

  public static async getCoworkSpace(
    coworkName: string
  ): Promise<ICoworkSpace> {
    return firebase
      .firestore()
      .collection(FirestoreCollectionNames.CoworkingSpaces)
      .doc(coworkName)
      .get()
      .then(ref => ref.data() as ICoworkSpace);
  }

  public static timestamp(): any {
    return firebase.firestore.FieldValue.serverTimestamp();
  }

  public static timestampFromDate(date: Date): any {
    return firebase.firestore.Timestamp.fromDate(date);
  }

  public static observeBranchUpdates(
    coworkName: string,
    branchName: string,
    onUpdate: (branch: ICoworkBranch) => void
  ): () => void {
    if (!coworkName || !branchName) {
      throw new Error(
        `Invalid cowork name ${coworkName} or branchName ${branchName}`
      );
    }

    const branchDoc = firebase
      .firestore()
      .collection(FirestoreCollectionNames.CoworkingSpaces)
      .doc(coworkName)
      .collection(FirestoreCollectionNames.CoworkBranches)
      .doc(branchName);

    const stopObserving = branchDoc.onSnapshot(branchSnapshot => {
      const branch = branchSnapshot.data() as ICoworkBranch;
      onUpdate(branch);
      console.log(`Updated branch! ${branch}`);
    });

    return stopObserving;
  }

  public static async getWeworkMember(
    memberId: string
  ): Promise<IWeworkMember> {
    if (!memberId) {
      return Promise.reject(`Invalid member id ${memberId}`);
    }
    const member = await firebase
      .firestore()
      .collection(FirestoreCollectionNames.WeworkMembers)
      .doc(memberId)
      .get();

    if (!member.exists) {
      throw new Error(`Member with id${memberId} doesn't exists. `);
    }

    return member.data() as IWeworkMember;
  }

  public static setUnassignedApplicationMetadata(
    metadata: IUnassignedApplicationMetadata
  ) {
    return firebase
      .firestore()
      .collection(FirestoreCollectionNames.UnassignedApplicationsMetadata)
      .doc(metadata.uid)
      .set(metadata, { merge: true });
  }

  public static saveCachedGeoLocation(location: IGooglePlace) {
    return firebase
      .firestore()
      .collection(FirestoreCollectionNames.CachedGeoLocations)
      .doc(location.id)
      .set(location);
  }

  public static async getCoworkGeoLocation(
    googlePlaceId: string
  ): Promise<ICoworkGeoLocation> {
    const googleKey: keyof ICoworkGeoLocation = "google";
    const idKey: keyof IGooglePlace = "id";
    const locations = await firebase
      .firestore()
      .collection(FirestoreCollectionNames.CoworksGeoLocations)
      .where(`${googleKey}.${idKey}`, "==", googlePlaceId)
      .get();

    if (locations.empty) {
      throw new Error("No location");
    } else {
      return locations.docs[0].data() as ICoworkGeoLocation;
    }
  }

  public static async assignApplication(
    application: IApplication,
    coworkName: string,
    branchName: string
  ) {
    const getCoworkPromise = this.getCoworkSpace(coworkName);
    const getBranchPromise = this.getCoworkingSpaceBranch(
      coworkName,
      branchName
    );

    const [cowork, branch] = await Promise.all([
      getCoworkPromise,
      getBranchPromise
    ]);

    const updatedApplication: IApplication = {
      ...application,
      coworkName: cowork.name,
      coworkDisplayName: cowork.displayName,
      branchDisplayName: branch.displayName,
      branchName: branch.name,
      branchState: branch.state,
      metadata: {
        ...application.metadata,
        wasAssignedByAdmin: true
      }
    };

    await firebase
      .firestore()
      .collection(FirestoreCollectionNames.CoworkingSpaces)
      .doc(coworkName)
      .collection(FirestoreCollectionNames.Applications)
      .doc(application.uid)
      .set(updatedApplication, { merge: true });

    console.log(
      `Saved new assigned application ${coworkName} ${application.uid}`
    );

    if (updatedApplication.isLeadApplication && updatedApplication.leadId) {
      const applicationPath : IApplicationPath = {
        id: application.uid,
        cowork: cowork.name
      }
      const updatedLead : Partial<ILead> = {
        applicationPath
      }
      await FirestoreService.updateLead(updatedApplication.leadId, updatedLead);
      console.log(
        `Updated leadID ${updatedApplication.leadId} with new path to assigned application`
      );
    }

    const unassginedMetadata: IUnassignedApplicationMetadata = {
      uid: application.uid!,
      location: {
        coworkName,
        branchName
      }
    };

    await FirebaseDatabaseService.setUnassignedApplicationMetadata(
      unassginedMetadata
    );

    await firebase
      .firestore()
      .collection(FirestoreCollectionNames.UnassignedApplications)
      .doc(application.uid)
      .delete();

    console.log(`Deleted unassigned application ${application.uid}`);
  }

  public static getObjectUpdates<T>(
    paths: string[],
    onUpdate: (data: T) => void,
    onError: (error: Error) => void
  ) {
    const db = firebase.firestore();
    const path = paths.join("/");

    const unsubscribe = db.doc(path).onSnapshot(doc => {
      const data = doc.data() as T;
      onUpdate(data);
    }, onError);

    return unsubscribe;
  }

  public static getListUpdates<T>(props: {
    paths: string[];
    onUpdate: (data: T[]) => void;
    onError: (error: Error) => void;
    limit?: number;
  }) {
    const { paths, onUpdate, onError } = props;
    const db = firebase.firestore();
    const path = paths.join("/");
    const unsubscribe = db.collection(path).onSnapshot(snapshot => {
      const docs = snapshot.docs.map(item => item.data() as T);
      onUpdate(docs);
    }, onError);

    return unsubscribe;
  }
}



// WEBPACK FOOTER //
// ./src/services/firebase/database/FirebaseDatabseService.ts